import { useCallback, useEffect, useMemo, useRef, useState, type JSX, type MouseEvent } from "react";
import type { components } from "../../api/schema";
import { loadClientEnv } from "../../config/env";
import { SegmentRenderer } from "./SegmentRenderer";
import type { SegmentRendererProps } from "./SegmentRenderer";
import type { CharacterEntry } from "./parseInlineMarkup";
import {
  ChoiceList,
  resolveChoiceThinkingCue,
  type ChoiceListChoice,
  type ChoicePreview,
  type ChoiceVisualCue,
} from "./ChoiceList";
import { AdvanceButton } from "./AdvanceButton";
import { SegmentProgressIndicator } from "./SegmentProgressIndicator";
import { useSegmentReveal } from "./useSegmentReveal";
import { useReaderStylePresetContext } from "../reader-style/ReaderStylePresetProvider";
import { VisualNovelLogModal } from "./VisualNovelLogModal";
import type { VisualNovelActorCue } from "./VisualNovelLayout";
import { resolveChoicePrompt } from "./resolveChoicePrompt";
import { resolveCutinImageUrl } from "./characterAssetPaths";

type Segment = components["schemas"]["Segment"];
type Character = components["schemas"]["Character"];

export type StreamingSceneViewProps = {
  segments: Segment[];
  // streaming-feature-parity follow-up 2.2.6: stream-schema の StreamChoice は Zod
  // .optional() 由来で `field?: T | undefined`、openapi-typescript 生成の Choice は
  // `field?: T` (exactOptionalPropertyTypes 準拠)。両者を受け入れるため ChoiceList 独自の
  // ChoiceListChoice 型 (両系統の subset) を使う。
  choices: ChoiceListChoice[] | null;
  isEnding: boolean | null;
  isStreaming: boolean;
  isPending: boolean;
  onSelectChoice: (choiceId: string) => void;
  onAdvance: () => void;
  /** キャラクター一覧。内部で Map に変換してセグメントの色付けに使う。省略時は空 Map 扱い。 */
  characters?: Character[];
  /** Sceneと同版の承認済み画像map。 */
  visualAssets?: components["schemas"]["VisualAssets"] | undefined;
  /**
   * worldline-palimpsest (Phase 3-1, Task 6.1) 用。モーダル等で scene 表示を
   * 一時停止したいとき true を渡すと autoAdvance タイマーを止め、クリック/タップ進行も無効化する。
   * false に戻ると本コンポーネント内部の state (visibleCount 等) は保持されたまま再開する。
   */
  paused?: boolean;
  /** ローディング中（isWaitingForFirstSegment）に選択済みカードとして表示する choice。 */
  selectedChoice?: ChoiceListChoice | undefined;
  /** visual_novel の生成待機中に、元の選択肢パネルを保持する。 */
  pendingChoices?: ChoiceListChoice[] | undefined;
  /** pendingChoices のうち選択済みとして強調する choice id。 */
  pendingChoiceId?: string | undefined;
  /** pendingChoices と一緒に残す side variant 用 prompt。 */
  pendingChoicePrompt?: string | undefined;
  /** visual_novel mode 用。true のとき auto timer を張らず click/tap だけで次 segment を表示する。 */
  manualReveal?: boolean;
  /** 親レイアウトから click/tap 進行を通知する単調増加 signal。 */
  advanceSignal?: number | undefined;
  /** StreamingSceneView 自身の click で reveal を進めるか。親レイアウトが click を持つ場合は false。 */
  clickAdvanceEnabled?: boolean;
  /** セグメントの見た目。visual_novel では名前欄付きの固定テキスト領域向け表示にする。 */
  segmentVariant?: SegmentRendererProps["variant"];
  /** visual_novel では現在の1文だけを表示し、ログはモーダルに分離する。 */
  segmentDisplayMode?: "accumulate" | "current";
  /** 表示済み文章を読むログボタンを表示する。 */
  logEnabled?: boolean;
  /** 親メニューからログを開くための単調増加 signal。 */
  logOpenSignal?: number | undefined;
  /** ログボタンの表示範囲。外部メニューに移す場合は none にする。 */
  logButtonVisibility?: "all" | "desktop" | "none";
  /** visual_novel 背景レイヤー用に、現在表示中の dialogue speakerId を親へ通知する。 */
  onCurrentSpeakerChange?: ((speakerId: string | null) => void) | undefined;
  /** visual_novel 背景レイヤー用に、現在表示中の dialogue actor cue を親へ通知する。 */
  onCurrentActorCueChange?: ((cue: VisualNovelActorCue | null) => void) | undefined;
  /** visual_novel 背景レイヤー用に、選択肢 preview を親へ通知する。 */
  onChoicePreviewChange?: ((preview: ChoicePreview | null) => void) | undefined;
  /** visual_novel 背景レイヤー用に、選択肢表示中の思考カットイン候補を親へ通知する。 */
  onChoiceThinkingCueChange?: ((cue: ChoiceVisualCue | null) => void) | undefined;
  /** 親レイアウト側で待機表示を出す場合、テキスト領域内の待機表示を抑止する。 */
  suppressWaitingIndicator?: boolean | undefined;
  /** Scenario の本文面色。choices や HUD には適用しない。 */
  panelBackgroundColor?: string | null | undefined;
};

// StreamingSceneView はストリーミング受信したセグメントを 1 件ずつ順番に表示する。
// reveal の進行ロジック (visibleCount / auto timer / click advance / 0→非空 reset /
// motion-reduce フォールバック) は `useSegmentReveal` hook に集約されている (paragraph-reveal Task 2.1)。
// 全セグメント表示済み + isStreaming=false + choices !== null で選択肢 UI を表示する（要件 4.3）。
export function StreamingSceneView({
  segments,
  choices,
  isEnding,
  isStreaming,
  isPending,
  onSelectChoice,
  onAdvance,
  characters = [],
  visualAssets,
  paused = false,
  selectedChoice,
  pendingChoices,
  pendingChoiceId,
  pendingChoicePrompt,
  manualReveal = false,
  advanceSignal,
  clickAdvanceEnabled = true,
  segmentVariant = "default",
  segmentDisplayMode = "accumulate",
  logEnabled = false,
  logOpenSignal,
  logButtonVisibility = "all",
  onCurrentSpeakerChange,
  onCurrentActorCueChange,
  onChoicePreviewChange,
  onChoiceThinkingCueChange,
  suppressWaitingIndicator = false,
  panelBackgroundColor,
}: StreamingSceneViewProps): JSX.Element {
  const [logOpen, setLogOpen] = useState(false);
  const [currentModeInteractiveVisible, setCurrentModeInteractiveVisible] =
    useState(false);
  const lastLogOpenSignalRef = useRef<number | undefined>(logOpenSignal);
  // Character[] → Map<id, CharacterEntry> に変換。
  // characters 参照が変わらない限り再計算しないようメモ化する。
  const charactersMap = useMemo<Map<string, CharacterEntry>>(() => {
    return new Map(
      characters.map((c) => [c.id, { name: c.name, color: c.color, portraitUrl: c.portraitUrl ?? null }]),
    );
  }, [characters]);

  // env flag は ChoiceList / ScenarioIntroOverlay と同様のパターン (loadClientEnv + useMemo)。
  // paragraphRevealEnabled=false のときは hook が即時全表示にフォールバックし、本 spec 導入前と
  // bit-by-bit 互換になる。
  const env = useMemo(() => loadClientEnv(), []);
  const { preset } = useReaderStylePresetContext();
  const { visibleCount, advance, allVisible } = useSegmentReveal({
    segments,
    paused,
    autoEnabled: env.paragraphRevealEnabled && !manualReveal,
    manualEnabled: manualReveal,
    pacing: preset.pacing,
  });
  const lastAdvanceSignalRef = useRef<number | undefined>(advanceSignal);
  const usesCurrentSegmentDisplay = segmentDisplayMode === "current";

  useEffect(() => {
    if (!allVisible || segments.length === 0) {
      setCurrentModeInteractiveVisible(false);
    }
  }, [allVisible, segments.length]);

  useEffect(() => {
    if (logOpenSignal === undefined) {
      lastLogOpenSignalRef.current = logOpenSignal;
      return;
    }
    const previous = lastLogOpenSignalRef.current;
    lastLogOpenSignalRef.current = logOpenSignal;
    if (previous === undefined || previous === logOpenSignal) {
      return;
    }
    if (logEnabled) {
      setLogOpen(true);
    }
  }, [logEnabled, logOpenSignal]);

  const advanceReveal = useCallback(() => {
    if (
      usesCurrentSegmentDisplay &&
      allVisible &&
      !currentModeInteractiveVisible
    ) {
      setCurrentModeInteractiveVisible(true);
      return;
    }
    advance();
  }, [
    advance,
    allVisible,
    currentModeInteractiveVisible,
    usesCurrentSegmentDisplay,
  ]);

  useEffect(() => {
    if (advanceSignal === undefined) {
      lastAdvanceSignalRef.current = advanceSignal;
      return;
    }
    const previous = lastAdvanceSignalRef.current;
    lastAdvanceSignalRef.current = advanceSignal;
    if (previous === undefined || previous === advanceSignal) {
      return;
    }
    advanceReveal();
  }, [advanceSignal, advanceReveal]);

  // クリック/タップで次のセグメントへ即座に進む。
  // 全セグメント表示済みの場合は選択肢 UI に処理を委ねる（クリックを無視する: hook 内で no-op）。
  // paused のときはクリック進行も無効化する (hook 内で no-op)。
  const handleClick = useCallback(() => {
    if (!clickAdvanceEnabled) return;
    advanceReveal();
  }, [advanceReveal, clickAdvanceEnabled]);

  // 全セグメント表示済み + ストリーミング完了 + choices あり で選択肢 UI を表示する
  const showChoices =
    allVisible &&
    (!usesCurrentSegmentDisplay || currentModeInteractiveVisible) &&
    !isStreaming &&
    choices !== null &&
    choices.length > 0;
  // 全セグメント表示済み + ストリーミング完了 + choices なし（エンディングまたは advance）で進行 UI を表示する
  const showAdvance =
    allVisible &&
    (!usesCurrentSegmentDisplay || currentModeInteractiveVisible) &&
    !isStreaming &&
    choices !== null &&
    choices.length === 0;
  const waitingChoices = pendingChoices ?? (selectedChoice !== undefined ? [selectedChoice] : undefined);
  const waitingChoiceId = pendingChoiceId ?? selectedChoice?.id;
  const shouldRenderWaitingChoices =
    isStreaming &&
    waitingChoices !== undefined &&
    waitingChoices.length > 0 &&
    waitingChoiceId !== undefined;
  const shouldHoldWaitingChoicesOnly =
    suppressWaitingIndicator && shouldRenderWaitingChoices;

  const visibleSegments = segments.slice(0, visibleCount);
  const showInteractive = showChoices || showAdvance;
  const displayedSegments = shouldHoldWaitingChoicesOnly
    ? []
    : usesCurrentSegmentDisplay && showInteractive
      ? []
      : usesCurrentSegmentDisplay
        ? visibleSegments.slice(-1)
        : visibleSegments;
  const currentDisplayedSegment =
    displayedSegments.length === 1 ? displayedSegments[0] : undefined;
  const currentDialogueSegment =
    segmentVariant === "visualNovel" &&
    currentDisplayedSegment?.type === "dialogue"
      ? currentDisplayedSegment
      : null;
  const currentSpeakerId = currentDialogueSegment?.speakerId ?? null;
  const currentActingVariantId = currentDialogueSegment?.actingVariantId;
  const currentCutinEnabled = currentDialogueSegment?.cutin;
  const currentCutinImageUrl = visualAssets?.mode === "versioned"
    ? currentCutinEnabled === true
      ? resolveCutinImageUrl(null, currentActingVariantId, visualAssets, currentSpeakerId ?? undefined) ?? undefined
      : undefined
    : currentDialogueSegment?.cutinImageUrl;
  const currentActorCue = useMemo<VisualNovelActorCue | null>(() => {
    if (currentSpeakerId === null) return null;
    return {
      speakerId: currentSpeakerId,
      ...(currentActingVariantId !== undefined
        ? { actingVariantId: currentActingVariantId }
        : {}),
      ...(currentCutinEnabled === true ? { cutin: true as const } : {}),
      ...(currentCutinImageUrl !== undefined
        ? { cutinImageUrl: currentCutinImageUrl }
        : {}),
    };
  }, [currentActingVariantId, currentCutinEnabled, currentCutinImageUrl, currentSpeakerId]);
  const showManualAdvanceCue =
    manualReveal &&
    visibleSegments.length > 0 &&
    (usesCurrentSegmentDisplay ? !showInteractive : !allVisible);
  const showLogButton =
    logEnabled && logButtonVisibility !== "none" && visibleSegments.length > 0;
  const choiceThinkingCue = useMemo<ChoiceVisualCue | null>(() => {
    if (!showChoices || choices === null) return null;
    if (visualAssets?.mode === "versioned") return choices.find((choice) => choice.visualCue !== undefined)?.visualCue ?? null;
    return resolveChoiceThinkingCue(choices, characters[0]?.id);
  }, [characters, choices, showChoices, visualAssets]);
  const choicePrompt = useMemo(() => {
    if (!showChoices || choices === null || !usesCurrentSegmentDisplay) return undefined;
    return resolveChoicePrompt({
      segments,
      choices,
      protagonist: characters[0],
    });
  }, [characters, choices, segments, showChoices, usesCurrentSegmentDisplay]);

  const openLog = useCallback((event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setLogOpen(true);
  }, []);

  useEffect(() => {
    onCurrentSpeakerChange?.(currentSpeakerId);
    onCurrentActorCueChange?.(currentActorCue);
  }, [
    currentActorCue,
    currentSpeakerId,
    onCurrentActorCueChange,
    onCurrentSpeakerChange,
  ]);

  useEffect(() => {
    return () => {
      onCurrentSpeakerChange?.(null);
      onCurrentActorCueChange?.(null);
    };
  }, [onCurrentActorCueChange, onCurrentSpeakerChange]);

  useEffect(() => {
    onChoiceThinkingCueChange?.(choiceThinkingCue);
  }, [choiceThinkingCue, onChoiceThinkingCueChange]);

  useEffect(() => {
    return () => {
      onChoiceThinkingCueChange?.(null);
    };
  }, [onChoiceThinkingCueChange]);

  // セグメントが届く前（初期待機状態）
  const isWaitingForFirstSegment = isStreaming && segments.length === 0;
  const waitingChoiceList = shouldRenderWaitingChoices ? (
    <ChoiceList
      choices={waitingChoices}
      chosenChoiceId={waitingChoiceId}
      isPending={true}
      onSelect={() => {}}
      charactersMap={charactersMap}
      variant={usesCurrentSegmentDisplay ? "side" : "default"}
      {...(usesCurrentSegmentDisplay && pendingChoicePrompt !== undefined
        ? { choicePrompt: pendingChoicePrompt }
        : {})}
    />
  ) : null;
  const waitingIndicatorOuterClassName = "flex min-h-48 items-center justify-center py-8";
  const waitingIndicatorInnerClassName =
    "flex w-full max-w-md flex-col items-center gap-4 rounded-lg border border-white/20 bg-slate-950/88 px-6 py-7 text-center shadow-[0_24px_80px_rgba(0,0,0,0.42)] backdrop-blur-md";
  const waitingIndicator = isWaitingForFirstSegment && !suppressWaitingIndicator ? (
    <div
      data-testid="streaming-waiting-indicator"
      className={waitingIndicatorOuterClassName}
      role="status"
      aria-live="polite"
      aria-label="物語を生成しています"
    >
      <div className={waitingIndicatorInnerClassName}>
        <span className="text-xl font-semibold tracking-normal text-slate-50 drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)] md:text-3xl">
          物語を紡いでいます
        </span>
        {/* 羽ペン + 1行×14ドット */}
        <div className="relative" style={{ width: "156px", height: "36px" }}>
          {/* 羽ペン（feather quill）: 横方向にのみ steps でジャンプ */}
          <div
            className="absolute motion-reduce:hidden"
            aria-hidden="true"
            style={{
              left: "2px",
              top: "2px",
              animation: "penWriteRow14 2.8s steps(1, end) infinite",
            }}
          >
            <svg
              width="20"
              height="24"
              viewBox="0 0 20 24"
              fill="none"
              className="text-reader-brand opacity-80"
              aria-hidden="true"
            >
              {/* 羽根部分 */}
              <path
                d="M18 1 C14 3, 8 8, 4 18 L6 19 C9 11, 14 6, 18 1Z"
                fill="currentColor"
                opacity="0.6"
              />
              <path
                d="M18 1 C16 5, 13 10, 8 16 L10 17 C14 12, 16 6, 18 1Z"
                fill="currentColor"
                opacity="0.3"
              />
              {/* ペン軸 */}
              <line
                x1="4" y1="18" x2="2" y2="23"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              {/* ペン先 */}
              <path d="M2 23 L3 21 L1 21 Z" fill="currentColor" />
            </svg>
          </div>
          {/* 1行 × 14ドット: ペン通過順に出現。右端到達で全消し */}
          <div
            className="absolute flex gap-[2px] motion-reduce:hidden"
            style={{ top: "18px", left: "0", animation: "dotRowFade 2.8s ease-in-out infinite" }}
          >
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].map((col) => (
              <span
                key={col}
                className="dot-type text-reader-secondary"
                aria-hidden="true"
                style={{
                  width: "8px",
                  height: "14px",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "11px",
                  lineHeight: "1",
                  animationDelay: `${col * 0.2}s`,
                }}
              >
                .
              </span>
            ))}
          </div>
        </div>
        {waitingChoiceList !== null && (
          <div className="w-full">
            {waitingChoiceList}
          </div>
        )}
      </div>
    </div>
  ) : null;

  // 新しいセグメントが表示されるたびに最下部へスクロール
  // rAF で 1 フレーム遅延させてレイアウト計算完了後に scrollHeight を取得する
  useEffect(() => {
    if (usesCurrentSegmentDisplay) return;
    // paused（モーダル表示中など）の間はモーダル背後でスクロールさせない
    // （PR #677 レビュー NIT 対応）。解除時に effect が再実行され最下部へ追いつく。
    if (paused) return;
    const id = requestAnimationFrame(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    });
    return () => cancelAnimationFrame(id);
  }, [paused, visibleCount, segments.length, showChoices, usesCurrentSegmentDisplay]);

  return (
    <div
      data-testid="streaming-scene-view"
      className="flex flex-col gap-4"
      onClick={handleClick}
    >
      {waitingIndicator}
      {suppressWaitingIndicator && waitingChoiceList}
      {showLogButton && (
        <div className="flex justify-end">
          <button
            type="button"
            data-testid="visual-novel-log-button"
            onClick={openLog}
            className={
              (logButtonVisibility === "desktop" ? "hidden md:inline-flex " : "") +
              "min-h-9 items-center rounded border border-reader-border-strong bg-reader-surface px-3 py-1.5 text-sm text-reader-secondary hover:bg-reader-border/60 hover:text-reader-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
            }
          >
            ログ
          </button>
        </div>
      )}
      {displayedSegments.map((segment, index) => {
        const sourceIndex = usesCurrentSegmentDisplay
          ? visibleCount - displayedSegments.length + index
          : index;
        const isLast = sourceIndex === visibleSegments.length - 1;
        return (
          <div
            key={`${sourceIndex}-${segment.type}`}
            className={
              segmentVariant === "visualNovel"
                ? "segment-enter"
                : "segment-enter flex items-end gap-1"
            }
          >
            <SegmentRenderer
              segment={segment}
              charactersMap={charactersMap}
              visualAssets={visualAssets}
              variant={segmentVariant}
              panelBackgroundColor={panelBackgroundColor}
            />
            {/* ストリーミング中は最終セグメント末尾に「生成中」インジケータを表示する（要件 4.5） */}
            {isLast && <SegmentProgressIndicator isStreaming={isStreaming} />}
          </div>
        );
      })}
      {showManualAdvanceCue && (
        <div
          data-testid="visual-novel-advance-cue"
          className="flex justify-end pr-1 pt-1"
          aria-hidden="true"
        >
          <span className="block size-2 rotate-45 border-b-2 border-r-2 border-reader-muted opacity-70" />
        </div>
      )}

      {showChoices && (
        <>
          {!usesCurrentSegmentDisplay && (
            <hr className="border-reader-border opacity-60 my-3" />
          )}
          <ChoiceList
            choices={choices}
            isPending={isPending}
            onSelect={onSelectChoice}
            charactersMap={charactersMap}
            variant={usesCurrentSegmentDisplay ? "side" : "default"}
            {...(usesCurrentSegmentDisplay ? { choicePrompt } : {})}
            {...(usesCurrentSegmentDisplay && onChoicePreviewChange !== undefined
              ? { onPreviewChoiceChange: onChoicePreviewChange }
              : {})}
          />
        </>
      )}
      {showAdvance && (
        <AdvanceButton isPending={isPending} onAdvance={onAdvance} />
      )}
      {logOpen && (
        <VisualNovelLogModal
          segments={visibleSegments}
          charactersMap={charactersMap}
          onClose={() => { setLogOpen(false); }}
          visualAssets={visualAssets}
        />
      )}
    </div>
  );
}
